/**
 * A market can be applied to a business priority in one of two states, both encoded
 * directly in the comma-joined `markets` string that round-trips through the backend:
 *
 * - on  -> `EMEA`
 * - off -> `EMEA:OFF`
 *
 * Keeping turned-off markets in the list (instead of dropping them) preserves the
 * distinction between a market that was deliberately disabled and one that was never applied.
 */
export const MARKET_OFF_SUFFIX = ':OFF'

export const isMarketOff = (entry: string): boolean => entry.endsWith(MARKET_OFF_SUFFIX)

/** Strips the `:OFF` suffix, returning the bare market code (e.g. `EMEA:OFF` -> `EMEA`). */
export const getMarketCode = (entry: string): string =>
  isMarketOff(entry) ? entry.slice(0, -MARKET_OFF_SUFFIX.length) : entry

export const toMarketOff = (code: string): string => `${code}${MARKET_OFF_SUFFIX}`

/** Toggle a single market between on/off while preserving its presence in the list. */
export const toggleMarketStatus = (markets: string[], market: string): string[] => {
  if (!market) {
    return markets
  }

  const offEntry = toMarketOff(market)

  if (markets.includes(market)) {
    // currently on -> turn off (replace the plain code with `${code}:OFF`)
    return markets.map((entry) => (entry === market ? offEntry : entry))
  }

  if (markets.includes(offEntry)) {
    // currently off -> turn back on (strip the suffix in place)
    return markets.map((entry) => (entry === offEntry ? market : entry))
  }

  // not present at all -> turn on (append the plain code)
  return [...markets, market]
}

/** Turn every market in the list on or off, preserving each market's presence. */
export const setAllMarketsStatus = (markets: string[], status: 'on' | 'off'): string[] =>
  markets.map((entry) =>
    status === 'off' ? toMarketOff(getMarketCode(entry)) : getMarketCode(entry)
  )
